import { BILLING_RETRIES, BILLING_RETRY_DAYS, PLAN_VAT_PERCENT, formatPlanTokens, formatTry, splitVat, type BillingProviderStatusDto, type PlanId, type PlatformPlanBody, type PlatformPlanDto, type PlatformPlanSaveDto } from '@dbrain/shared';
import { Alert, Badge, Button, Field, Icons, Select, Skeleton } from '@dbrain/ui';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client.js';
import { keys, useCompanyZone } from '../../api/hooks.js';
import { modelName, type ModelRow } from '../../components/admin/providers.js';
import type { InstallationView } from '../admin/ModelsAdminPage.js';
import { ErrorAlert } from '../../components/ErrorAlert.js';
import { useCapabilities } from '../../experience.js';
import {
  bodyOf,
  checkDraft,
  digitsOf,
  draftOf,
  formatLira,
  groupDigits,
  parseLira,
  pendingRows,
  sameBody,
  type LimitField,
  type PlanCheck,
  type PlanDraft,
  type PlanField,
} from './plan-form.js';

/**
 * Platform › Paketler (Phase 16a, ADR-0023, 06 §28.4; B2cPlatformPaketler without its payment card, which comes with
 * subscriptions): the two plans side by side with their prices (VAT included, D-262) and limits, and Ücretsiz's
 * announced change with "Vazgeç" (D-270). "Değişiklikleri kaydet" sits in the page header, so the cards' fields live
 * in usePlansEditor, which the page holds; unsaved values survive a look at another tab.
 */
export const PLANS_KEY = ['platform', 'plans'] as const;
const ORDER: readonly PlanId[] = ['free', 'pro'];

type Edits = Partial<Record<PlanId, Partial<PlanDraft>>>;

export interface PlansEditor {
  plans: UseQueryResult<PlatformPlanDto[]>;
  draft: (plan: PlatformPlanDto) => PlanDraft;
  check: (plan: PlatformPlanDto) => PlanCheck;
  edit: (id: PlanId, changes: Partial<PlanDraft>) => void;
  /** a card differs from what is saved and every such card is valid */
  canSave: boolean;
  saving: boolean;
  save: () => void;
  saveError: unknown;
  /** what the last save did, plan by plan */
  saved: PlatformPlanSaveDto[] | null;
}

export function usePlansEditor(enabled: boolean): PlansEditor {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const queryClient = useQueryClient();
  const plans = useQuery({ queryKey: PLANS_KEY, queryFn: () => api<PlatformPlanDto[]>('GET', '/api/platform/plans'), enabled });
  const [edits, setEdits] = useState<Edits>({});
  const [saved, setSaved] = useState<PlatformPlanSaveDto[] | null>(null);
  const draft = (plan: PlatformPlanDto): PlanDraft => ({ ...draftOf(plan, locale), ...edits[plan.id] });
  const check = (plan: PlatformPlanDto) => checkDraft(plan.id, draft(plan), locale, plan.chatModels.length);
  const changed = ORDER.flatMap((id) => {
    const plan = plans.data?.find((p) => p.id === id);
    if (!plan || !edits[id]) return [];
    const { body } = check(plan);
    return body && sameBody(body, bodyOf(plan)) ? [] : [{ id, body }];
  });
  const items = changed.flatMap(({ id, body }) => (body ? [{ id, body }] : []));
  const mutation = useMutation({
    mutationFn: async (list: { id: PlanId; body: PlatformPlanBody }[]) => {
      const done: PlatformPlanSaveDto[] = [];
      try {
        for (const item of list) done.push(await api<PlatformPlanSaveDto>('PUT', `/api/platform/plans/${item.id}`, item.body));
      } finally {
        // a plan saved before a failure stays saved: its fields follow the server again
        if (done.length > 0) {
          setSaved(done);
          setEdits((current) => {
            const next = { ...current };
            for (const result of done) delete next[result.plan.id];
            return next;
          });
          queryClient.setQueryData<PlatformPlanDto[]>(PLANS_KEY, (old) => old?.map((p) => done.find((r) => r.plan.id === p.id)?.plan ?? p));
        }
      }
      return done;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: PLANS_KEY });
      // the catalogue people see (planKeys.catalog)
      void queryClient.invalidateQueries({ queryKey: ['plans'] });
    },
  });
  return {
    plans,
    draft,
    check,
    edit: (id, values) => {
      setEdits((current) => ({ ...current, [id]: { ...current[id], ...values } }));
      setSaved(null);
      // a new edit clears the last save's error; a save on its way finishes first
      if (!mutation.isPending) mutation.reset();
    },
    canSave: changed.length > 0 && items.length === changed.length && !mutation.isPending,
    saving: mutation.isPending,
    save: () => {
      if (items.length > 0 && items.length === changed.length) mutation.mutate(items);
    },
    saveError: mutation.error,
    saved,
  };
}

/** "Değişiklikleri kaydet" in the page header */
export function PlansSaveButton({ editor }: { editor: PlansEditor }) {
  const { t } = useTranslation();
  return (
    <Button onClick={editor.save} disabled={!editor.canSave} busy={editor.saving} data-testid="plans-save">
      {t('b2cPlatform.plans.save')}
    </Button>
  );
}

export function PlansTab({ editor }: { editor: PlansEditor }) {
  const { t } = useTranslation();
  const capabilities = useCapabilities();
  const queryClient = useQueryClient();
  const [cancelled, setCancelled] = useState(false);
  const cancel = useMutation({
    mutationFn: (changeId: string) => api('DELETE', `/api/platform/plans/changes/${changeId}`),
    onMutate: () => setCancelled(false),
    onSuccess: () => setCancelled(true),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: PLANS_KEY }),
  });
  const plans = ORDER.flatMap((id) => editor.plans.data?.filter((p) => p.id === id) ?? []);
  // 16c.1: the models open in chat ("Sohbette" in Sağlayıcılar ve modeller), for the packages' choices
  const view = useQuery({ queryKey: keys.platform, queryFn: () => api<InstallationView>('GET', '/api/platform') });
  const models = (view.data?.providers ?? []).flatMap((p) => p.models.map((m) => ({ ...m, providerName: p.name })));
  return (
    <div className="stack b2c-tab">
      <div className="admin-info b2c-note" role="note" data-testid="plans-note">
        <Icons.info size={16} />
        <span>{t('b2cModels.plans.note')}</span>
      </div>
      {editor.saved ? (
        <Alert tone="success" title={t('b2cPlatform.plans.saved.title')} data-testid="plans-saved">
          {editor.saved.map((result) => (
            <p key={result.plan.id} data-applied={result.applied}>
              {t(`b2cPlatform.plans.saved.${result.applied}`, { plan: t(`b2cPlatform.planNames.${result.plan.id}`) })}
            </p>
          ))}
        </Alert>
      ) : null}
      {cancelled ? <Alert tone="success" title={t('b2cPlatform.plans.pending.cancelled')} data-testid="plan-pending-cancelled" /> : null}
      <ErrorAlert error={editor.plans.error ?? editor.saveError ?? cancel.error} />
      {!editor.plans.data && editor.plans.isPending ? <Skeleton block /> : null}
      {plans.length > 0 ? (
        <div className="plan-cards">
          {plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} editor={editor} models={models} cancelling={cancel.isPending} onCancelChange={(id) => cancel.mutate(id)} />
          ))}
        </div>
      ) : null}
      {capabilities.billing ? <PaymentCard /> : null}
    </div>
  );
}

/**
 * Phase 16c (B2cPlatformPaketler): the payment provider ("bağlı değil" until iyzico, Phase 16d; the fake one says it is
 * a test), what happens when a payment is not taken, and where the invoices are kept.
 */
function PaymentCard() {
  const { t } = useTranslation();
  const status = useQuery({ queryKey: ['platform', 'billing', 'provider'], queryFn: () => api<BillingProviderStatusDto>('GET', '/api/platform/billing/provider') });
  const check = useMutation({ mutationFn: () => status.refetch() });
  const provider = status.data;
  const state = !provider ? null : !provider.provider ? 'none' : provider.test ? 'test' : provider.connected ? 'live' : 'down';
  return (
    <section className="ui-card payment-card" aria-label={t('b2cBilling.plansCard.label')} data-testid="payment-card">
      <div className="payment-card__part">
        <h2 className="payment-card__title">{t('b2cBilling.plansCard.provider')}</h2>
        <p className="payment-card__value">
          {t(`b2cBilling.plansCard.providers.${provider?.provider ?? 'none'}`)}
          {state ? <Badge tone={state === 'live' ? 'success' : state === 'test' ? 'warning' : 'neutral'}>{t(`b2cBilling.plansCard.state.${state}`)}</Badge> : null}
        </p>
        <p className="payment-card__hint">{state === 'test' ? t('b2cBilling.plansCard.testHint') : state === 'none' ? t('b2cBilling.plansCard.noneHint') : t('b2cBilling.plansCard.keysHint')}</p>
        {provider?.provider ? (
          <button type="button" className="link-button" onClick={() => check.mutate()} disabled={check.isPending} data-testid="payment-check">
            {t('b2cBilling.plansCard.check')}
          </button>
        ) : null}
      </div>
      <div className="payment-card__part">
        <h2 className="payment-card__title">{t('b2cBilling.plansCard.failed')}</h2>
        <p className="payment-card__value">{t('b2cBilling.plansCard.retries', { count: BILLING_RETRIES, days: BILLING_RETRY_DAYS })}</p>
        <p className="payment-card__hint">{t('b2cBilling.plansCard.retriesHint')}</p>
      </div>
      <div className="payment-card__part">
        <h2 className="payment-card__title">{t('b2cBilling.plansCard.invoice')}</h2>
        <p className="payment-card__value">{t('b2cBilling.plansCard.invoiceValue', { vat: PLAN_VAT_PERCENT })}</p>
        <p className="payment-card__hint">{t('b2cBilling.plansCard.invoiceHint')}</p>
      </div>
    </section>
  );
}

/** A field of a card: its label, the value with its unit in one box, and a line under it (the VAT, or what is wrong) */
function PlanInput({
  label,
  value,
  onChange,
  onBlur,
  unit,
  hint,
  error,
  readOnly = false,
  wide = false,
  whole = false,
  placeholder,
  testId,
}: {
  label: string;
  value: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  unit?: ReactNode;
  hint?: string | undefined;
  error?: string | undefined;
  readOnly?: boolean;
  wide?: boolean;
  /** digits only (a count), not a price */
  whole?: boolean;
  /** 16c.1: what an empty field means ("Sınırsız") */
  placeholder?: string;
  testId: string;
}) {
  return (
    <div className={wide ? 'plan-field plan-field--wide' : 'plan-field'}>
      <Field label={label} hint={hint} error={error}>
        {({ id, describedBy, invalid }) => (
          <span className={['plan-field__box', readOnly ? 'plan-field__box--readonly' : '', invalid ? 'plan-field__box--invalid' : ''].join(' ').trim()}>
            <input
              id={id}
              className="plan-field__input"
              value={value}
              readOnly={readOnly}
              inputMode={readOnly ? undefined : whole ? 'numeric' : 'decimal'}
              autoComplete="off"
              placeholder={placeholder}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              onChange={(e) => onChange?.(e.target.value)}
              onBlur={onBlur}
              data-testid={testId}
            />
            {unit ? <span className="plan-field__unit">{unit}</span> : null}
          </span>
        )}
      </Field>
    </div>
  );
}

type PlatformModel = ModelRow & { providerName: string };

function PlanCard({
  plan,
  editor,
  models,
  cancelling,
  onCancelChange,
}: {
  plan: PlatformPlanDto;
  editor: PlansEditor;
  models: PlatformModel[];
  cancelling: boolean;
  onCancelChange: (changeId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const titleId = useId();
  const ownHintId = useId();
  const free = plan.id === 'free';
  const name = t(`b2cPlatform.planNames.${plan.id}`);
  const draft = editor.draft(plan);
  const { errors } = editor.check(plan);
  const edit = (values: Partial<PlanDraft>) => editor.edit(plan.id, values);
  const testId = (field: string) => `plan-${plan.id}-${field}`;
  const lira = t('b2cPlatform.plans.lira');
  const megabytes = t('b2cPlatform.plans.megabytes');

  const errorText = (field: PlanField): string | undefined => {
    const error = errors[field];
    if (!error) return undefined;
    if (error.kind !== 'min' && error.kind !== 'max') return t(`b2cPlatform.plans.errors.${error.kind}`);
    const bound =
      field === 'monthlyPriceKurus' || field === 'yearlyPriceKurus'
        ? formatTry(error.value, locale)
        : field === 'maxFileMb'
          ? `${groupDigits(String(error.value), locale)} ${megabytes}`
          : groupDigits(String(error.value), locale);
    return t(`b2cPlatform.plans.errors.${error.kind}`, { value: bound });
  };
  /** "KDV dahil: 416,67 + 83,33" under a valid price */
  const vatLine = (field: 'monthlyPriceKurus' | 'yearlyPriceKurus'): string | undefined => {
    const kurus = parseLira(draft[field], locale);
    if (kurus === null || kurus === 0 || errors[field]) return undefined;
    const { net, vat } = splitVat(kurus);
    return t('b2cPlatform.plans.vat', { net: formatLira(net, locale), vat: formatLira(vat, locale) });
  };
  const price = (field: 'monthlyPriceKurus' | 'yearlyPriceKurus', testName: string) => {
    const label = t(`b2cPlatform.plans.fields.${field}`);
    if (free) {
      // Ücretsiz has no price (the API refuses one): 0 TL a month, no yearly price
      return field === 'monthlyPriceKurus' ? (
        <PlanInput label={label} value="0" unit={lira} readOnly testId={testId(testName)} />
      ) : (
        <PlanInput label={label} value="—" readOnly testId={testId(testName)} />
      );
    }
    return (
      <PlanInput
        label={label}
        value={draft[field]}
        unit={lira}
        onChange={(value) => edit({ [field]: value })}
        onBlur={() => {
          // the typed price in the field's own form ("5000" → "5.000,00")
          const kurus = parseLira(draft[field], locale);
          const shown = kurus === null ? null : formatLira(kurus, locale);
          if (shown !== null && shown !== draft[field]) edit({ [field]: shown });
        }}
        hint={errorText(field) ? undefined : (vatLine(field) ?? (field === 'yearlyPriceKurus' && draft[field].trim() === '' ? t('b2cPlatform.plans.noYearly') : undefined))}
        error={errorText(field)}
        testId={testId(testName)}
      />
    );
  };
  const count = (field: 'dailyTokens' | 'maxDocuments' | 'maxFileMb', testName: string, digits: number, unit?: ReactNode, wide = false) => (
    <PlanInput
      label={t(`b2cPlatform.plans.fields.${field}`)}
      value={groupDigits(draft[field], locale)}
      unit={unit}
      wide={wide}
      whole
      onChange={(value) => edit({ [field]: digitsOf(value, digits) })}
      error={errorText(field)}
      testId={testId(testName)}
    />
  );
  const tokens = Number(draft.dailyTokens);
  // 16c.1 (B2cModelPaketler): own skills and connections; empty = no limit, 0 = none
  const own = (field: 'maxOwnSkills' | 'maxOwnConnections', testName: string, unit: string) => (
    <PlanInput
      label={t(`b2cModels.plans.fields.${field}`)}
      value={groupDigits(draft[field], locale)}
      placeholder={t('b2cModels.plans.unlimited')}
      unit={unit}
      whole
      onChange={(value) => edit({ [field]: digitsOf(value, 6) })}
      testId={testId(testName)}
    />
  );
  // the models open in chat, and any the package still lists
  const open = models.filter((m) => (m.kind === 'chat' && m.chatOffered) || draft.chatModels.includes(m.id));
  const modelError = errors.chatModels ? t(`b2cModels.plans.errors.${errors.chatModels.kind}`) : undefined;

  return (
    <section className="plan-card" aria-labelledby={titleId} data-testid={`plan-card-${plan.id}`}>
      <header className="plan-card__head">
        <h2 id={titleId} className="plan-card__name">
          {name}
        </h2>
        <Badge tone={free || plan.onSale ? 'success' : 'neutral'}>{t(free ? 'b2cPlatform.plans.status.free' : plan.onSale ? 'b2cPlatform.plans.status.onSale' : 'b2cPlatform.plans.status.notOnSale')}</Badge>
        <span className="plan-card__people" data-testid={testId('people')}>
          {t('b2cPlatform.plans.people', { count: plan.people, value: plan.people.toLocaleString(locale) })}
          {/* 16c.1: "Bana haber ver": the people waiting for the one mail when payments open */}
          {plan.interestWaiting !== null ? (
            <span className="plan-card__waiting" data-testid={testId('waiting')}>
              {t('b2cModels.plans.waiting', { count: plan.interestWaiting, value: plan.interestWaiting.toLocaleString(locale) })}
            </span>
          ) : null}
        </span>
      </header>
      {plan.pendingChange ? <PendingChange plan={plan} busy={cancelling} onCancel={onCancelChange} /> : null}
      <div className="plan-fields">
        {price('monthlyPriceKurus', 'monthly-price')}
        {price('yearlyPriceKurus', 'yearly-price')}
        {count('dailyTokens', 'daily-tokens', 12, draft.dailyTokens && tokens > 0 ? formatPlanTokens(tokens, locale) : undefined, true)}
        {count('maxDocuments', 'max-documents', 7)}
        {count('maxFileMb', 'max-file-mb', 4, megabytes)}
        {own('maxOwnSkills', 'own-skills', t('b2cModels.plans.units.skills'))}
        {own('maxOwnConnections', 'own-connections', t('b2cModels.plans.units.connections'))}
        <p id={ownHintId} className="plan-fields__hint">
          {t('b2cModels.plans.ownHint')}
        </p>
      </div>
      <PlanModels plan={plan} draft={draft} models={open} error={modelError} onChange={(changes) => edit(changes)} />
    </section>
  );
}

/** "Qwen (vLLM) · dengeli, her iş için", "big_llm · LiteLLM": where a model runs and its line (B2cModelPaketler) */
function modelMeta(model: PlatformModel, locale: string): string {
  const line = model.description ? model.description.charAt(0).toLocaleLowerCase(locale) + model.description.slice(1) : null;
  const alias = model.displayName && !model.modelId.includes('/') ? model.modelId : null;
  return [alias, model.providerName, line].filter(Boolean).join(' · ');
}

/**
 * 16c.1 (B2cModelPaketler): Ücretsiz's one chat model, or Pro's models and the one a new chat starts with. Only the
 * models open in chat are offered; a change reaches the pickers at once (Pro's removal waits 30 days for subscribers).
 */
function PlanModels({
  plan,
  draft,
  models,
  error,
  onChange,
}: {
  plan: PlatformPlanDto;
  draft: PlanDraft;
  models: PlatformModel[];
  error: string | undefined;
  onChange: (changes: Partial<PlanDraft>) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const titleId = useId();
  const free = plan.id === 'free';
  if (free) {
    return (
      <div className="plan-models" data-testid="plan-free-models">
        <Field label={t('b2cModels.plans.freeModel.title')} hint={t('b2cModels.plans.freeModel.hint')} error={error}>
          {({ id, describedBy, invalid }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={draft.chatModels[0] ?? ''}
              onChange={(e) => onChange({ chatModels: e.target.value ? [e.target.value] : [], defaultChatModel: e.target.value })}
              data-testid="plan-free-chat-model"
            >
              {draft.chatModels.length === 0 ? <option value="">{t('b2cModels.plans.freeModel.none')}</option> : null}
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {`${modelName(m)} · ${m.providerName}`}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
    );
  }
  const chosen = models.filter((m) => draft.chatModels.includes(m.id));
  const toggle = (id: string, on: boolean) => {
    const next = on ? [...draft.chatModels, id] : draft.chatModels.filter((x) => x !== id);
    // the list keeps the order of Sağlayıcılar ve modeller
    const ordered = models.map((m) => m.id).filter((x) => next.includes(x));
    onChange({ chatModels: ordered, defaultChatModel: ordered.includes(draft.defaultChatModel) ? draft.defaultChatModel : (ordered[0] ?? '') });
  };
  return (
    <div className="plan-models" role="group" aria-labelledby={titleId} data-testid="plan-pro-models">
      <h3 id={titleId} className="plan-models__title">
        {t('b2cModels.plans.proModels.title')}
      </h3>
      <p className="plan-models__hint">{t('b2cModels.plans.proModels.hint')}</p>
      {error ? (
        <p className="field-error" data-testid="plan-pro-models-error">
          {error}
        </p>
      ) : null}
      <ul className="plan-models__list">
        {models.map((m) => (
          <li key={m.id}>
            <label className="plan-model" data-testid={`plan-pro-model-${m.modelId}`}>
              <input type="checkbox" checked={draft.chatModels.includes(m.id)} onChange={(e) => toggle(m.id, e.target.checked)} />
              <span className="plan-model__text">
                <span className="plan-model__name">
                  {modelName(m)}
                  {draft.defaultChatModel === m.id && draft.chatModels.includes(m.id) ? <span className="tag-pill">{t('b2cModels.plans.proModels.default')}</span> : null}
                </span>
                <span className="plan-model__meta">{modelMeta(m, locale)}</span>
              </span>
            </label>
          </li>
        ))}
      </ul>
      <label className="plan-models__default">
        <span>{t('b2cModels.plans.proModels.newChat')}</span>
        <Select value={draft.defaultChatModel} onChange={(e) => onChange({ defaultChatModel: e.target.value })} disabled={chosen.length === 0} data-testid="plan-pro-default-model">
          {chosen.map((m) => (
            <option key={m.id} value={m.id}>
              {modelName(m)}
            </option>
          ))}
        </Select>
      </label>
    </div>
  );
}

/** Ücretsiz's stricter limits, announced and waiting (D-270): when, what changes, how many were told, and "Vazgeç" */
function PendingChange({ plan, busy, onCancel }: { plan: PlatformPlanDto; busy: boolean; onCancel: (changeId: string) => void }) {
  const { t, i18n } = useTranslation();
  const zone = useCompanyZone();
  const change = plan.pendingChange;
  if (!change) return null;
  const locale = i18n.language;
  const label = (field: LimitField) =>
    field === 'ownConnections' ? t('b2cPlatform.plans.ownConnections.title') : field === 'maxOwnSkills' || field === 'maxOwnConnections' ? t(`b2cModels.plans.fields.${field}`) : t(`b2cPlatform.plans.fields.${field}`);
  const value = (field: LimitField, v: number | boolean | null) =>
    v === null
      ? t('b2cModels.plans.unlimited')
      : typeof v === 'boolean'
      ? t(v ? 'b2cPlatform.plans.pending.on' : 'b2cPlatform.plans.pending.off')
      : field === 'dailyTokens'
        ? formatPlanTokens(v, locale)
        : field === 'maxFileMb'
          ? `${v.toLocaleString(locale)} ${t('b2cPlatform.plans.megabytes')}`
          : v.toLocaleString(locale);
  const date = new Date(change.effectiveAt).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: zone });
  const notices = t(change.noticesDone ? 'b2cPlatform.plans.pending.noticesDone' : 'b2cPlatform.plans.pending.noticesSending', {
    count: change.noticesSent,
    value: change.noticesSent.toLocaleString(locale),
  });
  return (
    <div className="plan-pending" data-testid={`plan-pending-${plan.id}`}>
      <Icons.time size={16} />
      <p className="plan-pending__text">
        <strong>{t('b2cPlatform.plans.pending.title', { date })}</strong>{' '}
        {pendingRows(plan)
          .map((row) => `${label(row.field)} ${value(row.field, row.from)} → ${value(row.field, row.to)}`)
          .join(' · ')}{' '}
        <span className="plan-pending__notices">({notices})</span>
      </p>
      <Button variant="secondary" size="sm" busy={busy} onClick={() => onCancel(change.id)} data-testid="plan-pending-cancel">
        {t('b2cPlatform.plans.pending.cancel')}
      </Button>
    </div>
  );
}
